<!-- Component for displaying a single Out of Area Protection (OAP) item for a plan -->
<template>
	<div
		v-if="mockProductOap"
		v-auto-animate
	>
		<!-- Parent item -->
		<div
			class="grid h-auto w-full cursor-pointer grid-cols-[minmax(0,1fr)_max-content] grid-rows-1 items-center gap-4 overflow-hidden"
			@click="expanded = !expanded"
		>
			<div class="flex flex-row-reverse items-center">
				<p class="text-secondary-900 flex-1 text-sm font-medium">
					{{ t('oap.transferGeneric') }}
				</p>
				<ShieldCheckIcon class="text-accent-600 mr-2 h-5 w-5" />
			</div>

			<ChevronDownIcon
				class="color-primary-800 size-6"
				:class="expanded && 'rotate-180'"
			/>
		</div>

		<!-- Local transfer -->
		<div
			v-if="expanded"
			class="mt-1 grid w-full grid-cols-[max-content_minmax(0,1fr)_max-content] grid-rows-1 items-center gap-4 overflow-hidden"
		>
			<div class="h-10 w-10 max-w-10"></div>

			<div class="flex flex-row-reverse items-center">
				<p class="text-secondary-900 flex-1 text-sm font-medium">
					{{ t('oap.transferLocal') }}
				</p>
				<ShieldCheckIcon class="text-accent-600 mr-2 h-5 w-5" />
			</div>
			<div
				class="text-sm text-slate-500"
				:class="{ 'text-secondary-300! line-through': !isOapEnabled }"
			></div>
		</div>

		<!-- Static display of OAP if no plans are selected -->
		<div
			v-if="expanded && !isPlanSelected"
			class="grid w-full grid-cols-[max-content_minmax(0,1fr)_max-content] grid-rows-1 items-center gap-4 overflow-hidden"
		>
			<div class="h-10 w-10 max-w-10"></div>

			<div class="flex flex-row-reverse items-center">
				<p class="text-secondary-900 flex-1 text-sm font-medium">
					{{ t('oap.oap') }}
				</p>
				<ShieldCheckIcon class="text-accent-600 mr-2 h-5 w-5" />
			</div>
		</div>

		<!-- Toggleable display of OAP if any plans are selected -->
		<SwipeableToggle
			v-else-if="expanded"
			target-class="bg-background-50!"
			v-model="isOapEnabled"
		>
			<div
				class="grid w-full grid-cols-[max-content_minmax(0,1fr)_max-content] grid-rows-1 items-center gap-4 overflow-hidden"
			>
				<div class="h-10 w-10 max-w-10"></div>

				<div class="flex flex-row-reverse items-center">
					<p
						class="text-secondary-900 flex-1 text-sm font-medium"
						:class="{ 'text-secondary-400! line-through': !isOapEnabled }"
					>
						{{ t('oap.oap') }}
					</p>
					<ShieldCheckIcon class="text-accent-600 mr-2 h-5 w-5" />
				</div>
			</div>
		</SwipeableToggle>
	</div>
</template>

<script setup lang="ts">
import type { PbLineItemPackage } from '@/apiTypes'
import SwipeableToggle from '@/components/common/SwipeableToggle.vue'
import { usePresentationLocale } from '@/i18n'
import { useMerchandise } from '@/mixins/merchandise'
import { useSessionStore } from '@/stores/session'
import { ChevronDownIcon, ShieldCheckIcon } from '@heroicons/vue/24/solid'
import { ref, computed, type PropType } from 'vue'

const props = defineProps({
	pkg: {
		type: Object as PropType<PbLineItemPackage>,
		required: true,
	},
	modelValue: {
		type: Boolean,
		default: false,
	},
})

const { mockProductOap } = useMerchandise()
const { t } = usePresentationLocale()
const sessionStore = useSessionStore()

const expanded = ref(false)

// Get plans
const plans = computed(() => sessionStore.activePlans ?? [])

// Check if any plans are selected
const isPlanSelected = computed(() =>
	plans.value.some((plan) =>
		plan.cartItems.some((item) => item.lineItemId === props.pkg.id)
	)
)

// Emit an event to update the local oap value
const emit = defineEmits(['update:modelValue'])

// Get oap local value, set it and then emit it
const isOapEnabled = computed({
	get: () => props.modelValue,
	set: (val: boolean) => emit('update:modelValue', val),
})
</script>
